import { NotificationHeader, NotificationTextMessage } from '@/core/enums/notification';
import { store } from '@/core/store';
import { showNotification, type NotificationKind } from '@/core/notify/notificationSlice';

function emit(type: NotificationKind, header: string, message: string): void {
  store.dispatch(showNotification({ type, header, message }));
}

/** Low-level notifier (Angular `NotificationService`). `keepAfterRouteChange` was inert in Angular and is dropped. */
export const notifier = {
  success: (header: string, message: string) => emit('success', header, message),
  error: (header: string, message: string) => emit('error', header, message),
  info: (header: string, message: string) => emit('info', header, message),
  warn: (header: string, message: string) => emit('warning', header, message),
};

/** Reads RFC 7807 problem details (including field errors) into one readable message. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function errorMessage(error: any): string {
  if (typeof error === 'string') {
    return error;
  }
  // A message the app raised itself (new Error('...')); an HTTP failure carries its problem details in `error` instead.
  if (error instanceof Error && error.constructor === Error && error.message && !('error' in error)) {
    return error.message;
  }
  const body = error?.error;
  if (Array.isArray(body?.errors) && body.errors.length > 0) {
    return body.errors.map((e: { message: string }) => e.message).join(' ');
  }
  if (body?.errors && typeof body.errors === 'object') {
    const messages = Object.values(body.errors).flat() as string[];
    if (messages.length > 0) {
      return messages.join(' ');
    }
  }
  return body?.detail ?? body?.title ?? NotificationTextMessage.errorMessage;
}

export function notifySuccess(message: string): void {
  notifier.success(NotificationHeader.success, message);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function notifyError(error: any): void {
  notifier.error(NotificationHeader.error, errorMessage(error));
}

/**
 * A failed download returns its problem details as a Blob; read the text out of it so the toast can say
 * something better than the default message.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function notifyBlobError(error: any, fallback: string): void {
  const body = error?.error;
  if (!(body instanceof Blob)) {
    notifyError(error ?? fallback);
    return;
  }

  body
    .text()
    .then((text) => {
      let detail: string | null = null;
      try {
        const parsed = text ? JSON.parse(text) : null;
        detail = parsed?.detail ?? parsed?.title ?? parsed?.message ?? null;
      } catch {
        // not JSON: fall through to the fallback text
      }
      notifyError(detail || fallback);
    })
    .catch(() => notifyError(fallback));
}
